/**
 * workspaceIdentity.ts — shared workspace display identity helpers
 * (multi-org plan D4/§10). Extracted from UserLoginCard (2026-08-16) so the
 * header pill and the login card render workspace identity from ONE
 * implementation; UserLoginCard re-exports `workspaceLabel` for its
 * existing consumers/tests.
 */
import type { WorkspaceOption } from './rbacService';

/**
 * Human label for a workspace row/chip — NEVER a raw account id (the
 * 2026-08-09 raw-redirect lesson). Fallback order: displayName →
 * "your workspace" (their own account) → truncated id as last resort.
 */
export function workspaceLabel(ws: WorkspaceOption, uid?: string | null): string {
  const name = (ws.displayName || '').trim();
  if (name && name !== ws.accountId) return name;
  if (ws.isOwner && (ws.via === 'self_account' || (!!uid && ws.accountId === uid))) {
    return 'your workspace';
  }
  // Owner directive 2026-08-16: NEVER an account id, not even truncated —
  // "it looks like an organization ID that's not helpful." The server now
  // resolves names from profile/main too, so this fallback is rare; when it
  // fires, the deterministic color mark still disambiguates rows.
  return 'unnamed workspace';
}

/**
 * Workspace mark palette (owner directive 2026-08-18): the dots must sit in
 * the prest0 scheme — muted, warm-anchored tones around the house gold —
 * instead of arbitrary full-spectrum hues. Every entry is mid-tone so the
 * mark reads on both the light (#FAFAF8) and dark (#1A1A1A) surfaces.
 */
export const WORKSPACE_MARK_PALETTE = [
  'var(--brand-gold)', // house gold
  '#9A6B38', // bronze
  '#B85C43', // terracotta
  '#A34E4E', // clay
  '#8E4F63', // wine
  '#7C5A80', // plum
  '#57678A', // slate
  '#47797A', // teal
  '#68804C', // moss
  '#8F8440', // olive
] as const;

function markSlot(accountId: string): number {
  let h = 0;
  for (let i = 0; i < accountId.length; i += 1) {
    h = (h * 31 + accountId.charCodeAt(i)) >>> 0;
  }
  return h % WORKSPACE_MARK_PALETTE.length;
}

/**
 * Deterministic per-roster color map (invariant 3: two orgs must never look
 * alike at a glance). Ids are assigned their hashed palette slot; within one
 * viewer's roster, collisions linear-probe to the next free slot (sorted ids,
 * so the outcome is stable for a given roster set across sessions/devices).
 * Rosters larger than the palette wrap and may share.
 */
export function workspaceMarkColors(rosterIds: string[]): Map<string, string> {
  const n = WORKSPACE_MARK_PALETTE.length;
  const taken = new Set<number>();
  const out = new Map<string, string>();
  for (const id of Array.from(new Set(rosterIds)).sort()) {
    let slot = markSlot(id);
    if (taken.size < n) {
      while (taken.has(slot)) slot = (slot + 1) % n;
    }
    taken.add(slot % n);
    out.set(id, WORKSPACE_MARK_PALETTE[slot % n]);
  }
  return out;
}

/** Deterministic prest0-palette color for one workspace mark. Pass the
 *  viewer's full roster when rendering alongside other workspaces so
 *  collision avoidance keeps every visible dot distinct. */
export function workspaceMarkColor(accountId: string, rosterIds?: string[]): string {
  if (rosterIds && rosterIds.length > 0) {
    const ids = rosterIds.includes(accountId) ? rosterIds : [...rosterIds, accountId];
    return workspaceMarkColors(ids).get(accountId) || WORKSPACE_MARK_PALETTE[markSlot(accountId)];
  }
  return WORKSPACE_MARK_PALETTE[markSlot(accountId)];
}

/** The native org is the user's own uid-keyed workspace (plan §1). */
export function isNativeWorkspace(ws: WorkspaceOption, uid?: string | null): boolean {
  return Boolean(uid && ws.accountId === uid);
}
